<script setup lang="ts">
/**
 * Text that turns into a field in place — renaming a sheet tab, a file in a
 * tree, a widget title. Double-click (or a single click with
 * `activation="click"`), Enter or F2 starts; Enter or leaving the field saves,
 * Escape restores the old text. The field grows with its content, so the
 * surrounding layout barely moves.
 *
 * Saving emits `update:modelValue` and `commit` only for a real change: an
 * unchanged text just closes, an empty one (with `required`) restores the old
 * text. `validate` may refuse a value — Enter then keeps the field open with
 * the error, leaving the field drops the draft. `selectBaseName` preselects a
 * file name without its extension.
 *
 * `save` hands a valid value to the caller, who stores it (a request to the
 * server): the field stays open, read-only, until it settles, and a refusal —
 * an error text returned or thrown („Den Namen gibt es schon“) — keeps it open
 * with that error instead of closing. Only a saved value updates the model.
 *
 * `v-model:editing` opens and closes it from outside (a menu's „Umbenennen“).
 * The default slot renders the resting text: `{ value, start }`.
 */
import Pencil from 'lucide-vue-next/dist/esm/icons/pencil.js';
import { computed, nextTick, ref, useId, watch } from 'vue';
import { NxIconButton } from '../IconButton';

const props = withDefaults(
  defineProps<{
    /** Shown while the value is empty. */
    placeholder?: string;
    /** Accessible name of the field. Defaults to „Name bearbeiten“. */
    ariaLabel?: string;
    /** What starts editing with the mouse. */
    activation?: 'dblclick' | 'click';
    /** Show a pencil button next to the text (visible on hover and focus). */
    showButton?: boolean;
    /** Label and tooltip of the pencil button. */
    editLabel?: string;
    /** Screen-reader hint on the resting text. */
    hint?: string;
    /** The resting text takes keyboard focus (Enter / F2 start). Turn off inside a focusable parent. */
    focusable?: boolean;
    disabled?: boolean;
    /** Trim the draft before saving. */
    trim?: boolean;
    /** An empty draft restores the old value instead of saving. */
    required?: boolean;
    /** Preselect up to the last dot („bericht“ of „bericht.pdf“). */
    selectBaseName?: boolean;
    maxlength?: number;
    /** Returns an error text for a value that may not be saved. */
    validate?: (value: string) => string | null | undefined;
    /**
     * Stores a valid, changed value; resolves to an error text (or throws) when
     * it was refused — the field then stays open with the error.
     */
    save?: (value: string) => Promise<string | null | undefined | void>;
  }>(),
  {
    placeholder: '',
    ariaLabel: 'Name bearbeiten',
    activation: 'dblclick',
    showButton: false,
    editLabel: 'Umbenennen',
    hint: 'Doppelklick, Enter oder F2 zum Bearbeiten',
    focusable: true,
    disabled: false,
    trim: true,
    required: true,
    selectBaseName: false,
    maxlength: undefined,
    validate: undefined,
    save: undefined,
  }
);

const emit = defineEmits<{
  /** A changed, valid value was saved. */
  commit: [value: string];
  /** Editing ended without saving (Escape, invalid on blur). */
  cancel: [];
}>();

const model = defineModel<string>({ default: '' });
const editing = defineModel<boolean>('editing', { default: false });

const draft = ref('');
const error = ref<string | null>(null);
/** `save` is running: the field is read-only and stays open. */
const saving = ref(false);
const inputRef = ref<HTMLInputElement | null>(null);
const displayRef = ref<HTMLElement | null>(null);
const hintId = useId();
const errorId = useId();
/** Keyboard ended the edit — hand focus back to the resting text. */
let refocus = false;

const hintText = computed(() =>
  props.activation === 'click' ? props.hint.replace(/^Doppelklick/, 'Klick') : props.hint
);

watch(
  editing,
  async (open) => {
    if (!open) return;
    draft.value = model.value;
    error.value = null;
    await nextTick();
    const input = inputRef.value;
    if (!input) return;
    input.focus();
    const dot = draft.value.lastIndexOf('.');
    if (props.selectBaseName && dot > 0) input.setSelectionRange(0, dot);
    else input.select();
  },
  { immediate: true }
);

function start() {
  if (props.disabled || editing.value) return;
  editing.value = true;
}

async function close() {
  editing.value = false;
  error.value = null;
  if (!refocus) return;
  refocus = false;
  await nextTick();
  displayRef.value?.focus();
}

/** Saves the draft; `false` when `validate` refused it (or a save still runs). */
function commit(): boolean {
  if (saving.value) return false;
  const value = props.trim ? draft.value.trim() : draft.value;
  if ((props.required && value === '') || value === model.value) {
    void close();
    return true;
  }
  const refusal = props.validate?.(value);
  if (refusal) {
    error.value = refusal;
    return false;
  }
  if (props.save) {
    void persist(value, props.save);
    return true;
  }
  finish(value);
  return true;
}

function finish(value: string) {
  model.value = value;
  emit('commit', value);
  if (editing.value) void close();
}

/** Runs `save`; a refusal keeps the field open with its error (and the focus, after Enter). */
async function persist(value: string, save: NonNullable<typeof props.save>) {
  const viaKeyboard = refocus;
  error.value = null;
  saving.value = true;
  let refusal: string | null | undefined | void;
  try {
    refusal = await save(value);
  } catch (err) {
    refusal = err instanceof Error ? err.message : String(err);
  } finally {
    saving.value = false;
  }
  if (!refusal) {
    finish(value);
    return;
  }
  refocus = false;
  if (!editing.value) return;
  error.value = refusal;
  if (!viaKeyboard) return;
  await nextTick();
  inputRef.value?.focus();
}

function cancel() {
  emit('cancel');
  void close();
}

function onInputKeydown(event: KeyboardEvent) {
  if (event.isComposing) return;
  if (event.key === 'Enter') {
    event.preventDefault();
    refocus = true;
    if (!commit()) refocus = false;
  } else if (event.key === 'Escape') {
    if (saving.value) return;
    // An open dialog around the field must not close with it.
    event.preventDefault();
    event.stopPropagation();
    refocus = true;
    cancel();
  }
}

function onBlur() {
  if (!editing.value || saving.value) return;
  if (!commit()) cancel();
}

function onDisplayKeydown(event: KeyboardEvent) {
  if (event.key !== 'Enter' && event.key !== 'F2') return;
  event.preventDefault();
  start();
}

function onDisplayClick(event: MouseEvent) {
  if (props.activation === (event.type === 'dblclick' ? 'dblclick' : 'click')) start();
}

defineExpose({ start, cancel });
</script>

<template>
  <span
    class="nx-inline-edit"
    :class="{
      'is-editing': editing,
      'is-disabled': disabled,
      'is-invalid': error,
      'is-saving': saving,
    }"
  >
    <template v-if="!editing">
      <span
        ref="displayRef"
        class="nx-inline-edit__display"
        :class="{ 'is-empty': !model }"
        :tabindex="focusable && !disabled ? 0 : undefined"
        :aria-describedby="focusable && !disabled ? hintId : undefined"
        data-testid="nx-inline-edit-display"
        @click="onDisplayClick"
        @dblclick="onDisplayClick"
        @keydown="onDisplayKeydown"
      >
        <slot :value="model" :start="start">{{ model || placeholder }}</slot>
      </span>
      <span :id="hintId" class="nx-inline-edit__sr-only">{{ hintText }}</span>
      <NxIconButton
        v-if="showButton"
        class="nx-inline-edit__button"
        height="xs"
        :title="editLabel"
        :disabled="disabled"
        data-testid="nx-inline-edit-button"
        @click.stop="start"
      >
        <Pencil class="nxt-icon-xs" aria-hidden="true" />
      </NxIconButton>
    </template>
    <span v-else class="nx-inline-edit__field" :data-value="draft || placeholder">
      <input
        ref="inputRef"
        v-model="draft"
        class="nx-inline-edit__input"
        type="text"
        size="1"
        :placeholder="placeholder"
        :maxlength="maxlength"
        :readonly="saving"
        :aria-busy="saving || undefined"
        :aria-label="ariaLabel"
        :aria-invalid="error ? 'true' : undefined"
        :aria-describedby="error ? errorId : undefined"
        data-testid="nx-inline-edit-input"
        @input="error = null"
        @keydown="onInputKeydown"
        @blur="onBlur"
      />
      <span v-if="error" :id="errorId" class="nx-inline-edit__error" role="alert">
        {{ error }}
      </span>
    </span>
  </span>
</template>

<style scoped>
.nx-inline-edit {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  min-width: 0;
  max-width: 100%;
  vertical-align: baseline;
}

.nx-inline-edit__display {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  border-radius: var(--nxt-radius-sm);
  outline: none;
  cursor: text;

  &.is-empty {
    color: var(--nxt-input-placeholder);
  }

  &:focus-visible {
    box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
  }

  .is-disabled & {
    cursor: default;
  }
}

.nx-inline-edit__button {
  flex-shrink: 0;
  opacity: 0;
  transition: opacity var(--nxt-motion-fast) var(--nxt-ease-out);
}

.nx-inline-edit:hover .nx-inline-edit__button,
.nx-inline-edit:focus-within .nx-inline-edit__button {
  opacity: 1;
}

@media (hover: none) {
  .nx-inline-edit__button {
    opacity: 1;
  }
}

/* Input and a hidden copy of its text share one grid cell, so the field is
   exactly as wide as what is typed. */
.nx-inline-edit__field {
  position: relative;
  display: inline-grid;
  min-width: calc(var(--nxt-size-unit) * 3);
  max-width: 100%;

  &::after {
    content: attr(data-value) ' ';
    grid-area: 1 / 1;
    visibility: hidden;
    white-space: pre;
    overflow: hidden;
    padding-inline: var(--nxt-space-2xs);
    border: var(--nxt-hairline) solid transparent;
  }
}

.nx-inline-edit__input {
  grid-area: 1 / 1;
  width: 100%;
  min-width: 0;
  margin: 0;
  padding: 0 var(--nxt-space-2xs);
  font: inherit;
  /* A record's value: the design's ink axis may colour it (I-2510). */
  color: var(--nxd-ink, var(--nxt-input-fg));
  background: var(--nxt-input-bg-focus);
  border: var(--nxt-hairline) solid var(--nxt-input-border-focus);
  border-radius: var(--nxt-radius-sm);
  outline: none;
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-input-ring);

  &::placeholder {
    color: var(--nxt-input-placeholder);
  }

  .is-saving & {
    color: var(--nxt-fg-muted);
    cursor: progress;
  }

  .is-invalid & {
    border-color: var(--nxt-input-border-invalid);
    box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-input-ring-invalid);
  }
}

.nx-inline-edit__error {
  position: absolute;
  top: calc(100% + var(--nxt-space-2xs));
  left: 0;
  z-index: var(--nxt-z-float);
  width: max-content;
  max-width: var(--nxt-panel-width-sm);
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-regular);
  line-height: var(--nxt-line-height-tight);
  white-space: normal;
  color: var(--nxt-danger-subtle-fg);
  background: var(--nxt-danger-subtle-bg);
  border: var(--nxt-hairline) solid var(--nxt-danger-border);
  border-radius: var(--nxt-radius-sm);
  box-shadow: var(--nxt-shadow-sm);
}

.nx-inline-edit__sr-only {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  padding: 0;
  margin: calc(var(--nxt-hairline) * -1);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

@media (prefers-reduced-motion: reduce) {
  .nx-inline-edit__button {
    transition: none;
  }
}
</style>
